import React, { Component } from 'react';
import { connect } from 'react-redux';
import { translate } from '@haldor/ui';

import {
	createConversation,
	getConversation,
	getConversationsByReference,
	submitMessage,
} from 'actions/conversation';

import ConversationMessages from 'containers/Conversation/Display/ConversationMessages';
import MessageForm from 'containers/Conversation/Form/MessageForm';
import Modal from 'containers/Modals/Modal';

import { Spinner } from 'UI';

const MessageFormAny = /** @type {any} */ (MessageForm);

class Message extends Component {

	/* Class setup */
	constructor(props) {
		super(props);
		this.hasCompletedInitialLoad = false;

		this.state = {
			loading: true,
			open: '',
		};
	}

	/* Lifecycle methods */
	componentDidMount = async () => {
		await this.fetchReferenceConversations(this.props);

		await this.loadConversationForActiveUser(this.props);
		this.hasCompletedInitialLoad = true;
		this.setState({ loading: false });
	}

	fetchReferenceConversations = (props) => {
		const { assignment, plan, meetingInstance, post } = props;

		if (assignment != null) {
			return this.props.getConversationsByReference(assignment.id, 'ASSIGNMENT');
		}

		if (plan != null) {
			return this.props.getConversationsByReference(plan.id, 'PLAN');
		}

		if (meetingInstance != null) {
			return this.props.getConversationsByReference(meetingInstance.id, 'MEETING_INSTANCE');
		}

		if (post != null) {
			return this.props.getConversationsByReference(post.id, 'POST');
		}

		return Promise.resolve();
	}

	normalizeUser = (user) => {
		if (user == null) {
			return null;
		}

		if (user.id != null) {
			return user;
		}

		return {
			...user,
			id: user.userId,
		};
	}

	findConversationForUser = (user, conversations = this.props.conversations) => {
		if (user == null || conversations == null) {
			return null;
		}

		const memberType = user.userType == "GUARDIAN" ? 'GUARDIAN' : 'USER';

		return conversations.find(conversation => {
			let membership = conversation.memberships.find(member => {
				return member.membershipType == memberType && member.membershipID == user.id;
			});

			return membership != null;
		}) || null;
	}

	loadConversationForActiveUser = async (props = this.props) => {
		const user = this.normalizeUser(props.activeUser);
		if (user == null) return;

		const conversation = this.findConversationForUser(user, props.conversations);
		if (conversation != null && conversation.id != 0) {
			await this.props.getConversation(conversation.id);
		}
	}

	UNSAFE_componentWillReceiveProps = (nextProps) => {
		if (nextProps.activeUser == null) {
			return false;
		}

		const userChanged = nextProps.activeUser != this.props.activeUser;
		const conversationsChanged = nextProps.conversations != this.props.conversations;

		// During initial mount, reference conversations update can arrive before
		// componentDidMount finishes and would otherwise trigger a duplicate load.
		if (!this.hasCompletedInitialLoad && conversationsChanged && !userChanged) {
			return false;
		}

		if (userChanged || conversationsChanged) {
			const user = this.normalizeUser(nextProps.activeUser);
			const conversation = this.findConversationForUser(user, nextProps.conversations);

			if (conversation != null && conversation.id != 0) {
				this.setState({ loading: true });
				this.props.getConversation(conversation.id).then(() => {
					this.setState({ loading: false });
				});
			} else if (userChanged) {
				this.setState({ loading: false });
			}
		}
	}

	/* Helper methods */
	getCreatorId = () => {
		let creator = null;

		if (this.props.assignment != null) {
			if (this.props.assignment.id == parseInt(this.props.assignment.id, 10)) {
				creator = this.props.assignment.creator;
			} else {
				creator = this.props.assignment.createdBy.user.id;
			}
		}

		if (this.props.plan != null) {
			creator = this.props.plan.creator;
		}

		if (this.props.meetingInstance != null) {
			creator = this.props.meetingInstance.creator;
		}

		if (this.props.post != null) {
			creator = this.props.post.creator;
		}

		return creator;
	}

	buildReference = () => {
		let references = [];
		let reference = {};

		if (this.props.assignment != null) {
			reference.referenceId = this.props.assignment.id;
			reference.referenceType = 'ASSIGNMENT';
		}

		if (this.props.plan != null) {
			reference.referenceId = this.props.plan.id;
			reference.referenceType = 'PLAN';
		}

		if (this.props.meetingInstance != null) {
			reference.referenceId = this.props.meetingInstance.id;
			reference.referenceType = 'MEETING_INSTANCE';
		}

		if (this.props.post != null) {
			reference.referenceId = this.props.post.id;
			reference.referenceType = 'POST';
		}

		if (reference.referenceType != null && reference.referenceId != null) {
			references.push(reference);
		}

		if (this.props.studentId != null && this.props.activeUser.userType == "GUARDIAN") {
			let studentReference = {};
			studentReference.referenceId = this.props.studentId;
			studentReference.referenceType = 'STUDENT';
			references.push(studentReference);
		}

		return references;
	}

	buildMemberships = (user) => {
		let memberships = [];

		memberships.push({
			membershipID: user.id,
			membershipType: user.userType == "GUARDIAN" ? 'GUARDIAN' : 'USER',
			permission: 'W',
		});

		memberships.push({
			membershipID: this.props.user.id,
			membershipType: 'USER',
			permission: 'O',
			user: this.props.user,
		});

		return memberships;
	}

	getConversationForUser = (user) => {
		return this.findConversationForUser(user);
	}

	getMembership = (conversation, userid) => {
		let membership = conversation.memberships.find(member => {
			return member.membershipType == 'USER' && member.membershipID == userid;
		});

		if (membership != null) {
			return membership;
		}

		return null;
	}

	/* Events */
	onCreate = async (values, student) => {
		values.creator = this.props.user.id;
		values.senderId = values.creator;
		values.senderType = 'USER';
		delete values.conversationID;

		let conversation = {
			messages: [values],
			memberships: this.buildMemberships(student),
			relationships: this.buildReference(),
			title: '',
			status: 'ACTIVE',
		};

		const conversationId = await this.props.createConversation(conversation);
		await this.props.getConversation(conversationId);
		await this.fetchReferenceConversations(this.props);
		this.setState({ loading: false });
		return 1;
	}

	submitMessage = async (values) => {
		const { user } = this.props;

		values.creator = user.id;
		values.senderId = values.creator;
		values.senderType = 'USER';

		await this.props.submitMessage(values);
		const conversationId = values.conversationID || (this.props.active != null ? this.props.active.id : null);
		if (conversationId != null) {
			await this.props.getConversation(conversationId);
		}

		return 1;
	}

	onModalClose = () => {
		if (this.props.onClose) {
			this.props.onClose();
		}
	}

	/* Render methods */
	renderConversation = () => {
		let conversation = null;
		let created = true;
		let user = this.props.activeUser;
		let trigger = this.props.activeUser.firstName + ' ' + this.props.activeUser.lastName;
		let disabled = false;

		if (user.id == null) {
			user.id = user.userId;
		}

		if (user != null) {
			conversation = this.getConversationForUser(user);
			if (conversation != null && conversation.status.toUpperCase() == 'ARCHIVED') {
				disabled = true;
			}
		}

		if (conversation != null && conversation.status.toUpperCase() == 'ARCHIVED') {
			disabled = true;
		}

		if (conversation == null) {
			trigger = this.props.translate('send-a-message');
			created = false;
			conversation = { id: 0 };
		}

		return <div className="conversation conversation--wrapper" style={{ boxShadow: 'none', borderRadius: 0, height: '70vh' }}>
			{created ?
				<ConversationMessages
					conversation={this.props.active}
				/>
				:
				<div className="conversation-begin" />
			}

			{created ?
				<MessageFormAny
					conversation={conversation}
					onSubmit={this.submitMessage}
					disabled={disabled}
				/>
				:
				<MessageFormAny
					conversation={null}
					onSubmit={values => this.onCreate(values, user)}
				/>
			}
		</div>
	}

	renderContent = () => {
		if (this.state.loading) {
			return <Spinner center />
		}

		return this.renderConversation();
	}

	render() {
		if (this.props.activeUser == null) {
			return null;
		}

		let title = null;
		if (this.props.plan != null) {
			title = this.props.activeUser.firstname + ' ' + this.props.activeUser.lastname;
		} else {
			title = this.props.activeUser.firstName + ' ' + this.props.activeUser.lastName;
		}

		return (
			<Modal type="small" overridePrompt onClose={this.onModalClose} isOpen={true} title={title}>
				<div className="single-assignment-message">
					{this.renderContent()}
				</div>
			</Modal>
		);
	}

}

function mapStateToProps(state) {
	return {
		translate: translate(state.Languages.translations),
		user: state.user.currentUser,
		conversations: state.Conversation.referenceConversations,
		active: state.Conversation.conversation,
		new: state.Conversation.new,
	};
}

export default connect(mapStateToProps, {
	getConversationsByReference,
	createConversation,
	submitMessage,
	getConversation,
})(Message);
